import {
  addCalendarDate,
  addDateTime,
  CalendarDate,
  calendarDateFromInstant,
  calendarDateTimeFromTimestamp,
  DateTime,
  DAY_START_TIME,
  fromCalendarDate,
  isAfterOrEqual,
  isBeforeOrEqual,
  isInstantAfterOrEqual,
  isInstantBefore,
} from './dateutils';

export const INVOICE_RUN_UTC_HOUR = 2;
export const INVOICE_RUN_UTC_MINUTE = 30;
export const DRAFT_GRACE_HOURS = 24;
// The daily run stamps the drafts it creates a few minutes after its scheduled time, while the next run is dated with
// its scheduled time: without this slack, an issuable draft born in a run would wait for the run after next.
const DRAFT_GRACE_RUN_SLACK_HOURS = 1;
const UTC_TIME_ZONE = 'UTC';
const MINUTES_IN_HOUR = 60;

const getInvoiceRunTime = (runDate: CalendarDate): DateTime =>
  addDateTime(
    calendarDateTimeFromTimestamp(`${fromCalendarDate(runDate)}${DAY_START_TIME}`),
    INVOICE_RUN_UTC_HOUR * MINUTES_IN_HOUR + INVOICE_RUN_UTC_MINUTE,
    'minutes'
  );

const getDraftGraceEnd = (createdAt: DateTime): DateTime =>
  addDateTime(createdAt, DRAFT_GRACE_HOURS - DRAFT_GRACE_RUN_SLACK_HOURS, 'hours');

/** True when a draft created at `createdAt` may be auto-validated by the run dated `runTime`. */
export const isPastDraftGrace = (createdAt: DateTime, runTime: DateTime): boolean =>
  isInstantAfterOrEqual(runTime, getDraftGraceEnd(createdAt));

/** The date of the last daily run that fetches a lease: it fetches a lease while its end date is on or after the
 * previous run's date. */
export const getLastInvoiceRunDate = (leaseEndDate: CalendarDate): CalendarDate =>
  addCalendarDate(leaseEndDate, 1, 'days');

export interface DraftAutoValidationRun {
  runTime: DateTime | null;
  isGraceBound: boolean;
}

/** The run that auto-validates a draft; null `runTime` when no run reaches it (the last run skips the grace). */
export const getDraftAutoValidationRun = (
  createdAt: DateTime,
  issueDate: CalendarDate,
  leaseEndDate: CalendarDate
): DraftAutoValidationRun => {
  const graceEnd = getDraftGraceEnd(createdAt);
  const graceEndDate = calendarDateFromInstant(graceEnd, UTC_TIME_ZONE);
  const firstRunDateAfterGrace = isInstantAfterOrEqual(getInvoiceRunTime(graceEndDate), graceEnd)
    ? graceEndDate
    : addCalendarDate(graceEndDate, 1, 'days');
  const isGraceBound = isAfterOrEqual(firstRunDateAfterGrace, issueDate);
  const runDate = isGraceBound ? firstRunDateAfterGrace : issueDate;
  const lastRunDate = getLastInvoiceRunDate(leaseEndDate);
  if (isBeforeOrEqual(runDate, lastRunDate)) {
    return { runTime: getInvoiceRunTime(runDate), isGraceBound };
  }
  const lastRunTime = getInvoiceRunTime(lastRunDate);
  if (isInstantBefore(createdAt, lastRunTime) && isBeforeOrEqual(issueDate, lastRunDate)) {
    return { runTime: lastRunTime, isGraceBound: true };
  }
  return { runTime: null, isGraceBound: false };
};
